import type { ContestType } from '@betterpool/api-types/contests-service';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useRouter } from 'next/router';

import { FEATURE } from '~/common/enums/feature.enum';
import { reportEvent } from '~/domains/analytics';
import { usePaginatedQuery } from '~/domains/common/hooks/usePaginatedQuery';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import type { TSortByDir, TSortByOption } from '~/domains/contest/domains/services/contest.service.api';
import type { PayoutType } from '~/domains/commissioner/interfaces/contest.interface';
import useIsUserAdmin from '~/domains/payments/hooks/useIsUserAdmin';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { useLocationKey } from '~/hooks/useLocationKey';
import { getContests } from '~/services/contests/contests.service.api';
import { SortDirection, SortOptions } from '~/services/contests/enums/contest-sort-options.enum';
import type { EntryType } from '~/services/contests/enums/entry-type.enum';

import { useFilterLeague } from '../Filters/useFilterLeague';
import { ViewStyle } from '../../Lobby.enums';

export const useLobby = () => {
  const [pageSize, setPageSize] = useState(50);
  const router = useRouter();

  const isJurisdictionInfoEnabled = useIsFeatureEnabled(FEATURE.JURISDICTION_ENABLE_INFO_LOBBY);
  const isAdmin = useIsUserAdmin();

    // ------------------ FILTERING ------------------
  const { locationKey, isLocationReady } = useLocationKey();

  const currentQuery = { ...router.query };
  const URLLeagueIdFilter = currentQuery[URLQueryKeys.LEAGUE] as string;
  const URLLeagueNameFilter = currentQuery[URLQueryKeys.LEAGUE_NAME] as string;
  const URLSortBy = currentQuery[URLQueryKeys.SORT_BY] as TSortByOption;
  const URLSortByOrder = currentQuery[URLQueryKeys.SORT_ORDER] as TSortByDir;
  const URLContestTypeFilter = currentQuery[URLQueryKeys.CONTEST_TYPE] as ContestType;
  const URLPayoutFormatFilter = currentQuery[URLQueryKeys.PAYOUT_FORMAT] as PayoutType;
  const URLEntryTypesFilter = currentQuery[URLQueryKeys.ENTRY_TYPES] as EntryType;
  const URLEntryFeeFilter = currentQuery[URLQueryKeys.ENTRY_FEE] as string;
  const URLHideUnlistedFilter = currentQuery[URLQueryKeys.HIDE_UNLISTED] as string;
  const URLViewAsFilter = currentQuery[URLQueryKeys.VIEW] as ViewStyle;
  const viewStyle = URLViewAsFilter || ViewStyle.TABLE;

  // ------------------ LEAGUE FILTER SUBHEADER TABS ------------------
  const {
    leagueFilterData,
    league: activeLeague,
    handleSetLeagueFilter,
  } = useFilterLeague({ isDisabled: false });

  useEffect(() => {
    if (URLLeagueNameFilter) {
      const league = leagueFilterData?.find(({ label }) => label === URLLeagueNameFilter);
      if (league) {
        handleSetLeagueFilter(league.value);
      }
    }
  }, [URLLeagueNameFilter]);

  const tabs = useMemo(
    () =>
      leagueFilterData?.map(({ value, label, isActive }) => ({
        onClick: handleSetLeagueFilter,
        value,
        label,
        isActive: activeLeague === value,
        isDisabled: !isActive,
      })),
    [leagueFilterData, activeLeague]
  );

  // ------------------ DATA QUERY ------------------
  const filterPayload = {
    contestTypes: URLContestTypeFilter?.split(',') as ContestType[],
    entryTypes: URLEntryTypesFilter?.split(',') as EntryType[],
    leagueId: URLLeagueIdFilter,
    payoutFormats: URLPayoutFormatFilter?.split(',') as PayoutType[],
    entryFee: URLEntryFeeFilter
      ? {
        min: Number(URLEntryFeeFilter.split(',')[0]) * 100,
        max: Number(URLEntryFeeFilter.split(',')[1]) * 100
      }
      : undefined,
  }
  const payload = {
    filter: filterPayload,
    // EDGE-30638: temporary $21M-push default. Revert after Sept 13 (EDGE-30642):
    // restore `sort: URLSortBy, dir: URLSortByOrder,`
    sort: URLSortBy ?? SortOptions.TOTAL_PRIZES,
    dir: URLSortByOrder ?? SortDirection.DESC,
    includeFull: isAdmin,
    hideUnlisted: !URLHideUnlistedFilter,
  };

  const allContests = usePaginatedQuery(
    {
      queryKey: ['contests', payload, locationKey],
      keepPreviousData: true,
      queryFn: ({ limit, offset }) => getContests({ ...payload, limit, offset }),
      enabled: isLocationReady,
    },
    {
      pageSize,
    }
  );

  const {
    data,
    error,
    isLoading,
    isPreviousData,
    isFetching,
    isInitialLoading,
    pagesTotal,
    itemsTotal,
    onCurrentPageChange,
    currentPage,
  } = allContests;

  // Detection if any of the visible contest are restricted in current location
  const hasRestrictedContests = useMemo(() => data?.data?.some(
    (contest) => contest.is_allowed_in_location === false
  ), [data?.data]);

  // ------------------ HANDLERS ------------------
  const handlePageChange = useCallback(
    (newPage: number) => {
      reportEvent('Lobby > Pagination', {
        page_number: newPage,
        results_per_page: pageSize,
        goto_first: newPage === 1,
        goto_last: newPage === pagesTotal,
      });
      onCurrentPageChange(newPage);
    },
    [onCurrentPageChange, pageSize, pagesTotal]
  );

  return {
    isAdmin,
    tabs,

    viewStyle,
    setLimit: setPageSize,
    page: currentPage,
    pages: pagesTotal,
    limit: pageSize,
    itemsTotal,

    data,
    error,
    isLoading,
    isPreviousData,
    isFetching,
    isInitialLoading,

    locationKey,
    hasRestrictedContests,
    isJurisdictionInfoEnabled,

    handlePageChange,
  };
};
